import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { featureGuard } from './core/guards/feature.guard';
import { guestGuard } from './core/guards/guest.guard';
import { HeaderConfig } from './core/models/header-config';

export const routes: Routes = [
  // Default redirect
  { path: '', redirectTo: '/home', pathMatch: 'full' },

  // Authentication routes (no auth required)
  {
    path: '',
    loadChildren: () => import('./routes/authentication.routes').then((m) => m.authenticationRoutes),
  },

  // Core application routes
  {
    path: 'home',
    loadComponent: () => import('./features/home/pages/home/home.component').then((mod) => mod.HomePage),
    data: { headerConfig: { showBackButton: false, titleSource: { type: 'logo' } } satisfies HeaderConfig },
    canActivate: [authGuard],
  },

  // Family management
  {
    path: 'manage-family',
    loadComponent: () =>
      import('./features/guardians/pages/manage-family/manage-family.component').then((mod) => mod.ManageFamilyPage),
    data: { headerConfig: { titleSource: { type: 'static', value: 'Manage my family' } } satisfies HeaderConfig },
    canActivate: [authGuard],
  },

  // Feature module routes with lazy loading
  {
    path: 'guardians',
    loadChildren: () => import('./routes/guardians.routes').then((m) => m.guardianRoutes),
  },
  {
    path: 'learners',
    loadChildren: () => import('./routes/learners.routes').then((m) => m.learnerRoutes),
  },
  {
    path: 'payments',
    loadChildren: () => import('./routes/finance.routes').then((m) => m.financeRoutes),
  },
  {
    path: 'notifications',
    loadChildren: () => import('./routes/notifications.routes').then((m) => m.notificationRoutes),
  },
  {
    path: 'help',
    loadChildren: () => import('./routes/help-and-support.routes').then((m) => m.helpRoutes),
  },
  {
    path: 'enrolments',
    loadChildren: () => import('./routes/enrolment.routes').then((m) => m.enrolmentRoutes),
  },
  {
    path: 'events',
    loadChildren: () => import('./routes/events.routes').then((m) => m.eventRoutes),
  },
  {
    path: 'preview/library',
    canActivate: [featureGuard('library')],
    loadComponent: () =>
      import('./features/library/pages/preview/preview.component').then((mod) => mod.PreviewPage),
    data: { headerConfig: { titleSource: { type: 'static', value: 'Preview' } } satisfies HeaderConfig },
  },
  {
    path: 'library',
    canActivate: [featureGuard('library')],
    loadChildren: () => import('./routes/resource.routes').then((m) => m.resourceRoutes),
  },
  {
    path: 'settings',
    loadChildren: () => import('./routes/settings.routes').then((m) => m.settingsRoutes),
  },

  // Standalone feature routes (single component, no sub-routing needed)
  {
    path: 'homework',
    loadComponent: () =>
      import('./features/homework/pages/approaching-deadlines/approaching-deadlines.component').then(
        (mod) => mod.ApproachingDeadlinesPage,
      ),
    data: { headerConfig: { titleSource: { type: 'static', value: 'Approaching deadlines' } } satisfies HeaderConfig },
    canActivate: [authGuard],
  },
  {
    path: 'inbox/:messageid',
    loadComponent: () =>
      import('./features/communication/pages/communication/communication.component').then(
        (mod) => mod.CommunicationComponent,
      ),
    data: { headerConfig: { titleSource: { type: 'static', value: 'Communication' } } satisfies HeaderConfig },
    canActivate: [authGuard],
  },
  {
    path: 'announcements',
    loadComponent: () =>
      import('./features/communication/pages/news-and-comms/news-and-comms.component').then(
        (mod) => mod.NewsAndCommsPage,
      ),
    data: { headerConfig: { titleSource: { type: 'static', value: 'Announcements' } } satisfies HeaderConfig },
    canActivate: [authGuard],
  },
  {
    path: 'faqs',
    loadComponent: () => import('./features/faqs/pages/faqs/faqs.component').then((mod) => mod.FaqsComponent),
    data: {
      headerConfig: { showHeader: false, titleSource: { type: 'static', value: 'FAQs' } } satisfies HeaderConfig,
    },
    canActivate: [guestGuard],
  },

  { path: '', redirectTo: '/home', pathMatch: 'full' },

  // Wildcard fallback route
  { path: '**', redirectTo: '/home' },
];
